/**
 * Sub-aba "Manual Foto": o ÚNICO arquivo (PDF) que a cliente baixa na aba "Manual Foto Modelo".
 * Subir um novo substitui o anterior. Porte de `admin.html:2081-2094` e `9373-9457`; linhas 140 a
 * 142 do inventário.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO: na substituição, o arquivo antigo é apagado do bucket
 * sem conferir se deu certo; e não há limite de tamanho aqui (o legado só confere a extensão).
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// O botão da plataforma.
// O botao e o visualizador de PDF, os dois do Chassi. O visualizador entrou em 07/10/2026, e o
// porque esta no bloco onde ele e desenhado, mais abaixo.
import { Botao, VisualizadorPdf } from '@complexity/ui';
// O nome aceito pelo Storage, com a regra do legado.
import { sanitizarNomeArquivo, type LinhaParametroManualFoto } from '@studiolu/dados';
// As chamadas da área.
import {
  apagarManualFoto,
  enviarArquivoAoBucket,
  inserirManualFoto,
  lerManualFoto,
  linkAssinado,
  removerArquivoDoBucket,
  substituirManualFoto,
} from '@studiolu/dados/areas/sistema';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área, e o status que some em 3 s (`mostrarStatusManualFotoParam`, `admin.html:9377`).
import { gravarNaMemoria, lerDaMemoria, useStatusDaArea } from './memoria.js';
// A linha compacta dos campos curtos.
import { LinhaCompacta } from '../financeiro/apoio.js';
// As peças visuais da área.
import { CampoArquivo, NotaSecao, TextoParam, type Carga, type PropsDaSubAba } from './pecas.js';

// Onde o arquivo escolhido espera, na memória da área (o `#manual-foto-param-novo-arquivo` do legado).
const CHAVE_DO_ARQUIVO = 'sistema:manual:arquivo';

// O arquivo atual com o link assinado, ou `null` quando não há arquivo.
interface ManualComLink {
  readonly linha: LinhaParametroManualFoto;
  readonly link: string;
}

export function SubAbaManualFoto({ confirmar, recarga }: Pick<PropsDaSubAba, 'confirmar' | 'recarga'>) {
  const { dados } = usePainel();
  const [carga, setCarga] = useState<Carga<ManualComLink | null>>({ tipo: 'carregando' });
  // A linha atual (`manualFotoParamAtual`), que o envio consulta para decidir entre trocar e inserir.
  const atual = useRef<LinhaParametroManualFoto | null>(null);
  // O parágrafo `#manual-foto-param-status`, fora da lista no legado (`admin.html:2093`): fica na
  // memória da área, como o arquivo escolhido (o `input` do legado é fixo da página).
  const [status, mostrarStatus] = useStatusDaArea('sistema:manual:status');

  // `renderizarManualFotoParam` (`admin.html:9392-9418`): assina o arquivo por uma hora.
  const desenhar = useCallback(async () => {
    const linha = atual.current;
    if (!linha) {
      setCarga({ tipo: 'pronta', dados: null });
      return;
    }
    const assinado = linha.arquivo_path ? await linkAssinado(dados, linha.arquivo_path) : null;
    // Sem assinatura, o link aponta para `#`, como no legado.
    setCarga({ tipo: 'pronta', dados: { linha, link: (assinado?.ok ? assinado.dados : null) || '#' } });
  }, [dados]);

  // `carregarManualFotoParam` (`admin.html:9383-9390`): a tabela pode estar vazia.
  const carregar = useCallback(async () => {
    setCarga({ tipo: 'carregando' });
    const resultado = await lerManualFoto(dados);
    if (!resultado.ok) {
      setCarga({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    atual.current = resultado.dados ?? null;
    await desenhar();
  }, [dados, desenhar]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // "Excluir" (`admin.html:9407-9417`): o bucket primeiro, e só depois a linha.
  const excluir = useCallback(async () => {
    const linha = atual.current;
    if (!linha) return;
    if (!(await confirmar('Excluir o arquivo do manual? A cliente deixa de ver o link de download.'))) return;
    const doBucket = await removerArquivoDoBucket(dados, linha.arquivo_path ?? '');
    if (!doBucket.ok) {
      mostrarStatus('Erro ao excluir: ' + doBucket.erro.mensagem);
      return;
    }
    const resultado = await apagarManualFoto(dados, linha.id);
    if (!resultado.ok) {
      mostrarStatus('Erro ao excluir: ' + resultado.erro.mensagem);
      return;
    }
    atual.current = null;
    await desenhar();
    mostrarStatus('Arquivo excluído.');
  }, [dados, confirmar, desenhar, mostrarStatus]);

  // "Enviar / Substituir" (`admin.html:9420-9457`).
  const enviar = useCallback(async () => {
    // O arquivo vem da memória da área, onde o `CampoArquivo` o guardou (o `input.files[0]` do legado).
    const arquivo = lerDaMemoria<File | null>(CHAVE_DO_ARQUIVO, null);
    if (!arquivo) {
      mostrarStatus('Escolhe um arquivo primeiro.');
      return;
    }
    // A extensão depois do último ponto, em minúsculas, tem que ser `pdf` (`admin.html:9424`).
    if ((arquivo.name.split('.').pop() ?? '').toLowerCase() !== 'pdf') {
      mostrarStatus('Só é permitido enviar arquivo em PDF.');
      return;
    }
    mostrarStatus('Enviando...');
    // O caminho do legado: a pasta do manual, o carimbo em milissegundos e o nome limpo.
    const caminho = `manual-foto-modelo/${Date.now()}_${sanitizarNomeArquivo(arquivo.name)}`;
    // Sem `upsert`: o carimbo já faz o caminho ser novo a cada envio.
    const envio = await enviarArquivoAoBucket(dados, caminho, arquivo, false);
    if (!envio.ok) {
      mostrarStatus('Erro ao enviar: ' + envio.erro.mensagem);
      return;
    }
    const anterior = atual.current;
    if (anterior) {
      // Já havia um: o antigo sai do bucket (sem conferir) e a MESMA linha é atualizada.
      await removerArquivoDoBucket(dados, anterior.arquivo_path ?? '');
      const resultado = await substituirManualFoto(dados, anterior.id, caminho, arquivo.name, new Date().toISOString());
      if (!resultado.ok) {
        mostrarStatus('Erro ao salvar: ' + resultado.erro.mensagem);
        return;
      }
      atual.current = resultado.dados;
    } else {
      // O primeiro arquivo: insere a linha.
      const resultado = await inserirManualFoto(dados, caminho, arquivo.name);
      if (!resultado.ok) {
        mostrarStatus('Erro ao salvar: ' + resultado.erro.mensagem);
        return;
      }
      atual.current = resultado.dados;
    }
    // O campo esvazia junto com a memória, como o `input.value = ''` do legado.
    gravarNaMemoria<File | null>(CHAVE_DO_ARQUIVO, null);
    await desenhar();
    mostrarStatus('Arquivo salvo.');
  }, [dados, desenhar, mostrarStatus]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Arquivo do manual que a cliente baixa na aba &quot;Manual Foto Modelo&quot;: sempre 1 único arquivo; subir um novo
        substitui o anterior.
      </NotaSecao>
      {/* O arquivo atual (`#manual-foto-param-atual`). */}
      <div data-testid="manual-atual">
        {carga.tipo === 'carregando' && <TextoParam>Carregando…</TextoParam>}
        {carga.tipo === 'erro' && <TextoParam>{`Erro ao carregar: ${carga.mensagem}`}</TextoParam>}
        {carga.tipo === 'pronta' && carga.dados === null && <TextoParam>Nenhum arquivo enviado ainda.</TextoParam>}
        {carga.tipo === 'pronta' && carga.dados !== null && (
          // A linha de arquivo do legado: nome à esquerda, "Excluir" à direita (`.arquivo-linha`).
          <div className="flex flex-wrap items-center justify-between gap-cx-3 border-b border-borda py-cx-3 text-cx-base [overflow-wrap:anywhere]">
            <span>
              <a href={carga.dados.link} target="_blank" rel="noopener noreferrer" className="text-marca underline underline-offset-4">
                {carga.dados.linha.arquivo_nome}
              </a>
            </span>
            <span className="flex shrink-0 items-center">
              {/* "Excluir" é um link vermelho sublinhado (`a.excluir-link`), como no legado. */}
              <a
                href="#"
                className="inline-flex min-h-toque items-center text-cx-sm text-erro underline underline-offset-4"
                onClick={(evento) => {
                  // O `#` não navega: é o `preventDefault` do legado.
                  evento.preventDefault();
                  void excluir();
                }}
              >
                Excluir
              </a>
            </span>
          </div>
        )}
        {/*
          O PDF NA TELA, logo abaixo do nome do arquivo.

          ACRESCIMO DECLARADO, E NAO PORTE: o `portal/admin.html` nao tem `iframe` nenhum (medido
          em 07/10/2026), entao o painel legado nunca mostrou o manual. Ele entra porque o pedido do
          fundador de 07/10/2026, 18:31, alcanca *"qualquer lugar que tenha que ver um pdf no
          sistema"*, e porque quem acabou de substituir o arquivo precisa conferir o que subiu.

          SO COM LINK DE VERDADE: `'#'` e o que o `desenhar` poe quando a assinatura falha, e
          desenhar o visualizador sobre ele daria a tela de falha da peca em cima de um endereco que
          nunca foi para lugar nenhum. O nome do arquivo acima continua aparecendo, que e a
          informacao que a consultora precisa para saber que existe arquivo cadastrado.
        */}
        {carga.tipo === 'pronta' && carga.dados !== null && carga.dados.link !== '#' && (
          <div className="mt-cx-4">
            <VisualizadorPdf
              endereco={carga.dados.link}
              titulo={carga.dados.linha.arquivo_nome ?? 'Manual da foto modelo'}
              testid="visualizador-do-manual"
            />
          </div>
        )}
      </div>
      <LinhaCompacta>
        {/* O seletor já filtra `.pdf`, e o clique confere de novo (`admin.html:2087`). */}
        <CampoArquivo chave={CHAVE_DO_ARQUIVO} rotulo="Arquivo do manual" accept=".pdf" />
        <Botao onClick={() => void enviar()}>Enviar / Substituir</Botao>
      </LinhaCompacta>
      <LinhaDeStatus texto={status} testid="status-manual" />
    </div>
  );
}
